import React, { useEffect, useMemo, useState } from 'react';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { formatCurrency } from "../../../utils/helper";

const List = () => {
    const [isLoader, setIsLoader] = useState(false);
    const [partners, setPartners] = useState([]);
    const [pagination, setPagination] = useState({});
    const [stats, setStats] = useState(null);
    const [apiData, setApiData] = useState({ search: "", page: 1 });

    // Payout modal state
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ method: 'Bank Transfer', reference: '', notes: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getPayouts();
    }, [apiData.page, apiData.search]);

    const getPayouts = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({ method: "GET", url: "admin/payouts/summary", data: apiData });
            const data = response.data;
            if (data.status) {
                setPartners(data.data);
                setPagination(data.pagination);
                setStats(data.stats || null);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // handled in ApiService
        } finally {
            setIsLoader(false);
        }
    };

    // Prefer API-provided totals; otherwise derive from the loaded rows.
    const summary = useMemo(() => {
        if (stats) return stats;
        return partners.reduce((acc, p) => ({
            total_earning: acc.total_earning + (Number(p.total_earning) || 0),
            total_paid: acc.total_paid + (Number(p.total_paid) || 0),
            pending_balance: acc.pending_balance + (Number(p.pending_balance) || 0),
            pending_partners: acc.pending_partners + (Number(p.pending_balance) > 0 ? 1 : 0),
        }), { total_earning: 0, total_paid: 0, pending_balance: 0, pending_partners: 0 });
    }, [stats, partners]);

    const handlePageChange = (page) => setApiData(prev => ({ ...prev, page }));
    const clearFilter = () => setApiData({ search: '', page: 1 });

    const openPayModal = (partner) => {
        setSelected(partner);
        setForm({ method: 'Bank Transfer', reference: '', notes: '' });
        setShowModal(true);
    };

    const closeModal = () => {
        if (saving) return;
        setShowModal(false);
        setSelected(null);
    };

    const submitPayout = async (e) => {
        e.preventDefault();
        if (!selected) return;
        try {
            setSaving(true);
            const response = await ApiService.request({
                method: "POST",
                url: "admin/payouts",
                data: { partner_id: selected._id, ...form },
            });
            const data = response.data;
            if (data.status) {
                toast.success(data.message);
                setShowModal(false);
                setSelected(null);
                getPayouts();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // handled in ApiService
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Partner Payouts
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Partner Payouts</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/admin/payouts/history" className="btn btn-light-primary btn-sm">
                            <i className="bi bi-clock-history me-1"></i> Payout History
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    {/* Summary Cards */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-3 col-sm-6">
                            <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <h5 className="text-gray-600 fw-semibold mb-0">Net Earnings</h5>
                                    <i className="bi bi-wallet2 fs-1 text-gray-400"></i>
                                </div>
                                <h2 className="fw-bold text-dark mb-0">{formatCurrency(summary.total_earning)}</h2>
                            </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                            <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <h5 className="text-gray-600 fw-semibold mb-0">Total Paid Out</h5>
                                    <i className="bi bi-cash-coin fs-1 text-success"></i>
                                </div>
                                <h2 className="fw-bold text-success mb-0">{formatCurrency(summary.total_paid)}</h2>
                            </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                            <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <h5 className="text-gray-600 fw-semibold mb-0">Pending Payouts</h5>
                                    <i className="bi bi-hourglass-split fs-1 text-warning"></i>
                                </div>
                                <h2 className="fw-bold text-warning mb-0">{formatCurrency(summary.pending_balance)}</h2>
                            </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                            <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <h5 className="text-gray-600 fw-semibold mb-0">Awaiting Payout</h5>
                                    <i className="bi bi-people fs-1 text-primary"></i>
                                </div>
                                <h2 className="fw-bold text-dark mb-0">{summary.pending_partners ?? 0}</h2>
                                <p className="text-muted mb-0 fs-7">partner(s) with balance</p>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <div className="row">
                                    <div className="col-md-3">
                                        <input
                                            type="text"
                                            value={apiData.search}
                                            onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                                            className="form-control form-control-solid"
                                            placeholder="Search by partner..."
                                        />
                                    </div>
                                    <div className="col-md-2 mt-2">
                                        <button
                                            type="button"
                                            onClick={clearFilter}
                                            className={`btn btn-sm btn-secondary ${!apiData.search ? 'd-none' : ''}`}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-0">
                            <div className="table-responsive">
                                <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                    <thead>
                                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                            <th>Partner</th>
                                            <th>Gross Earnings</th>
                                            <th>Commission</th>
                                            <th>Net Earnings</th>
                                            <th>Paid Out</th>
                                            <th>Pending Balance</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600 fs-6">
                                        {partners.length > 0 ? partners.map((p, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="symbol symbol-40px me-3">
                                                            <img
                                                                src={p.logo || '/assets/media/avatars/blank.png'}
                                                                alt={p.company_name}
                                                                className="rounded"
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <span className="text-dark fw-bold">{p.company_name || '-'}</span>
                                                            <span className="text-muted fs-7">{p.email}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{formatCurrency(p.total_gross)}</td>
                                                <td className="text-danger">{formatCurrency(p.total_commission)}</td>
                                                <td>{formatCurrency(p.total_earning)}</td>
                                                <td className="text-success">{formatCurrency(p.total_paid)}</td>
                                                <td>
                                                    <span className={`badge ${p.pending_balance > 0 ? 'badge-light-warning' : 'badge-light-success'} fs-7`}>
                                                        {formatCurrency(p.pending_balance)}
                                                    </span>
                                                    {p.pending_bookings > 0 && (
                                                        <span className="text-muted fs-8 ms-1">({p.pending_bookings})</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => openPayModal(p)}
                                                        className="btn btn-sm min-w-125px btn-base"
                                                        disabled={!(p.pending_balance > 0)}
                                                    >
                                                        <i className="bi bi-cash-coin me-1"></i> Pay Now
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="7" className="text-dark text-center mt-2">No data found!</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                {isLoader && <Loader text="Loading" size="30" className="text-center" />}
                                <span>Total Records: {pagination?.total_records}</span>
                                {pagination.total_pages > 1 && (
                                    <Paginator
                                        page={pagination.current_page}
                                        rows={pagination.per_page}
                                        totalRecords={pagination.total_records}
                                        onPageChange={handlePageChange}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pay Now Modal */}
            {showModal && selected && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1055 }}>
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <form onSubmit={submitPayout}>
                                    <div className="modal-header">
                                        <h5 className="modal-title">Record Payout</h5>
                                        <button type="button" className="btn-close" onClick={closeModal}></button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="d-flex justify-content-between align-items-center mb-4 p-4 bg-light rounded">
                                            <div>
                                                <div className="fw-bold text-dark">{selected.company_name}</div>
                                                <div className="text-muted fs-7">{selected.pending_bookings} pending booking(s)</div>
                                            </div>
                                            <div className="text-end">
                                                <div className="text-muted fs-8">Payout Amount</div>
                                                <div className="fw-bold fs-4 text-success">{formatCurrency(selected.pending_balance)}</div>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="fs-6 fw-semibold mb-2">Payment Method</label>
                                            <select
                                                className="form-select form-control-solid"
                                                value={form.method}
                                                onChange={e => setForm({ ...form, method: e.target.value })}
                                            >
                                                <option value="Bank Transfer">Bank Transfer</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Cheque">Cheque</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="fs-6 fw-semibold mb-2">Reference / Transaction ID</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-solid"
                                                value={form.reference}
                                                onChange={e => setForm({ ...form, reference: e.target.value })}
                                                placeholder="Optional"
                                            />
                                        </div>

                                        <div className="mb-1">
                                            <label className="fs-6 fw-semibold mb-2">Notes</label>
                                            <textarea
                                                className="form-control form-control-solid"
                                                rows="2"
                                                value={form.notes}
                                                onChange={e => setForm({ ...form, notes: e.target.value })}
                                                placeholder="Optional"
                                            ></textarea>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-light" onClick={closeModal} disabled={saving}>Cancel</button>
                                        <button type="submit" className="btn btn-base" disabled={saving}>
                                            <span hidden={saving}>Confirm Payout</span>
                                            {saving && <Loader size={20} text="Saving" />}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop fade show"
                        onClick={closeModal}
                        style={{ zIndex: 1050, backgroundColor: 'rgba(0, 0, 0, 0.6)', opacity: 1 }}
                    ></div>
                </>
            )}
        </div>
    );
};

export default List;
